import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useProfileState } from '@/contexts/ProfileContext';
import { analysis as analysisApi, pollJob } from '@/lib/api';
import { Spinner } from '@/components/atoms';
import { FlipCard } from '@/components/FlipCard';
import { CoverLetterModal } from '@/pages/Dashboard/RoleExplorer';
import { trackEvent, EVENTS, showQuickWinToast } from '@/lib/analytics';

export default function JDAnalyzer() {
  const {
    progress,
    toggleJdAction,
    markQuickWin,
    // JD job state — persisted at context level so it survives navigation
    pendingJdJob,
    pendingJdCompletedId,
    pendingJdErrored,
    startJdJob,
    clearJdJob,
    consumeJdCompletedId,
  } = useProfileState();

  const [jd, setJd] = useState('');
  const [currentJdId, setCurrentJdId] = useState(null);
  // `busy` covers TWO cases:
  //   1) A JD analysis is in flight (pendingJdJob != null) — background poll running
  //   2) The user is loading a previous analysis via openPrevious
  // We derive it from context + a small local `openingId` state so the loading
  // spinner keeps showing even when the user leaves the page and returns.
  const [openingId, setOpeningId] = useState(null);
  const busy = !!pendingJdJob || !!openingId;
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [tab, setTab] = useState(0);
  const [previous, setPrevious] = useState([]);
  const [loadingPrev, setLoadingPrev] = useState(true);
  const [coverOpen, setCoverOpen] = useState(false);
  // "finishing" = the background job is done and we're fetching/opening the
  // result. Lets the progress bar jump to 100% instead of vanishing.
  const [finishing, setFinishing] = useState(false);
  // Keep the last job's start time so the 100% completion frame still shows
  // a correct elapsed time after pendingJdJob clears.
  const lastJobStartRef = React.useRef(null);
  useEffect(() => {
    if (pendingJdJob?.started_at) lastJobStartRef.current = pendingJdJob.started_at;
  }, [pendingJdJob]);

  // Build a synthetic role object from the JD result so the CoverLetterModal
  // can generate against the exact job the user just analysed. Skills present
  // = requirements the CV covered strongly; skills gap = anything missing;
  // key_gap = the most-severe missing item.
  const jdRole = useMemo(() => {
    if (!result) return null;
    const reqs = result.requirements || [];
    const covered = reqs.filter((x) => x.status === 'strong' || x.status === 'covered').map((x) => x.req).filter(Boolean);
    const partial = reqs.filter((x) => x.status === 'partial').map((x) => x.req).filter(Boolean);
    const gaps = reqs.filter((x) => x.status === 'gap' || x.status === 'missing').map((x) => x.req).filter(Boolean);
    const dealbreaker = reqs.find((x) => (x.severity || '').toLowerCase() === 'dealbreaker' && (x.status === 'gap' || x.status === 'missing'));
    return {
      title: result.jd_title || 'the role',
      sector: result.jd_company || '',
      why: result.competitive_edge || '',
      skills_present: [...covered, ...partial].slice(0, 6),
      skills_gap: gaps.slice(0, 4),
      key_gap: (dealbreaker?.req) || (gaps[0]) || '',
    };
  }, [result]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await analysisApi.jdList();
        if (mounted) setPrevious(list.items || []);
      } catch {
        /* silently ignore — user will just start a new JD */
      } finally {
        if (mounted) setLoadingPrev(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Surface backend error from the background poll
  useEffect(() => {
    if (pendingJdErrored) {
      setError('JD analysis failed. Please try again.');
    }
  }, [pendingJdErrored]);

  // When the background poll finishes, auto-open the fresh result. We track
  // the last-opened id in a ref (instead of clearing context state up-front)
  // because `consumeJdCompletedId()` mutates `pendingJdCompletedId`, which is
  // also a dep of this effect — clearing it mid-flight re-fired the cleanup,
  // flipped `cancelled`, and silently dropped the fetched result (UI stuck on
  // "Opening your results…"). Context state is now cleared only AFTER the
  // result has rendered.
  const openedCompletedIdRef = React.useRef(null);
  useEffect(() => {
    const id = pendingJdCompletedId;
    if (!id || openedCompletedIdRef.current === id) return;
    openedCompletedIdRef.current = id;
    setFinishing(true);
    (async () => {
      try {
        const data = await analysisApi.jdGet(id);
        setResult(data.result);
        setCurrentJdId(data.id || id);
        setJd(data.jd_text || '');
        setTab(0);
        setError(null);
        // Instrumentation — a JD analysis completed. The Quick Win itself is
        // fired by the render-based effect below (it fires when the result
        // actually renders — not on click, not on background completion).
        try { trackEvent(EVENTS.jd_analysis_run, {}); } catch (_e) { /* non-critical */ }
        // Refresh the previous-analyses list so the newly completed one
        // shows up (and the "In progress" chip disappears).
        try {
          const list = await analysisApi.jdList();
          setPrevious(list.items || []);
        } catch { /* not critical */ }
      } catch {
        setError('JD analysis finished, but the result could not be loaded. It should still appear in Previous analyses.');
      } finally {
        setFinishing(false);
        consumeJdCompletedId();
      }
    })();
  }, [pendingJdCompletedId, consumeJdCompletedId]);

  // BUG-3 fix — mark the "Benchmark yourself against a real job posting"
  // Quick Win the moment a benchmark result successfully RENDERS (fresh run
  // or reopened analysis). Idempotent client- and server-side, persisted in
  // MongoDB, so it survives refresh and later visits.
  useEffect(() => {
    if (!result) return;
    (async () => {
      try {
        const res = await markQuickWin('jd_ran');
        if (res && res.newly_completed) {
          showQuickWinToast('Benchmark yourself against a real job posting');
        }
      } catch (_e) { /* non-critical */ }
    })();
  }, [result, markQuickWin]);

  const submitJD = async () => {
    if (jd.trim().length < 100) { setError('Paste a full job description (at least 100 characters).'); return; }
    setError(null);
    try {
      const { job_id } = await analysisApi.jdAsync(jd);
      // Hand off polling to ProfileContext so it survives navigation. The
      // `pendingJdJob` state renders the "In progress" chip in the sidebar
      // and the completion effect above auto-opens the result.
      startJdJob(job_id, jd.slice(0, 200));
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'JD analysis failed to start.');
    }
  };

  const openPrevious = async (id) => {
    setError(null); setOpeningId(id);
    try {
      const data = await analysisApi.jdGet(id);
      setResult(data.result);
      setCurrentJdId(data.id || id);
      setJd(data.jd_text || '');
      setTab(0);
    } catch {
      setError('Could not load previous analysis.');
    } finally { setOpeningId(null); }
  };

  const startNewJD = () => {
    setResult(null);
    setCurrentJdId(null);
    setJd('');
    setError(null);
  };

  return (
    <div>
      <h1 className="text-[30px] font-semibold text-sd-black mb-2 heading-tight">JD Analyzer</h1>
      <p className="text-sd-mid-gray text-[14.5px] mb-6">Paste a job description above to see how you stack up against it — realistic match score, coaching on gaps, cv recommendations, interview prep, LinkedIn optimization, plus a first-draft cover letter you can copy in one click.</p>

      {/* Previous analyses */}
      {!loadingPrev && (previous.length > 0 || pendingJdJob) && (
        <div className="sd-card p-4 mb-4" data-testid="jd-previous-panel">
          <div className="text-[12px] uppercase tracking-wider text-sd-mid-gray font-semibold mb-2">Previous analyses</div>
          <div className="flex flex-wrap gap-2">
            {/* "In progress" chip — shown while a JD job is running in the
                background. Renders at the front of the list so it's the first
                thing the user sees. Not clickable — the result will auto-open
                when it's ready. */}
            {pendingJdJob && (
              <span
                className="sd-chip inline-flex items-center gap-2 !cursor-default"
                style={{ background: '#F0F8F3', color: '#1F4D2A', borderColor: 'rgba(76,217,100,0.32)' }}
                data-testid="jd-prev-in-progress"
                title="Analysis in progress — this will open automatically when it finishes"
              >
                <Spinner size={12} color="#1F4D2A" />
                <span>
                  {pendingJdJob.jd_title_hint || 'New JD'} · <span className="font-semibold">In progress</span>
                </span>
              </span>
            )}
            {previous.map((p) => (
              <button key={p.id} className="sd-chip" onClick={() => openPrevious(p.id)} data-testid={`jd-prev-${p.id}`}>
                {p.jd_title || 'JD'} · {p.match_score ?? '—'}%
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="sd-card p-5 mb-5">
        <textarea
          value={jd}
          onChange={(e) => setJd(e.target.value)}
          className="sd-input min-h-[160px] font-mono text-[12.5px] !p-3"
          placeholder="Paste the full job description here — requirements, responsibilities, everything."
          data-testid="jd-textarea"
        />
        <div className="flex items-center justify-between mt-3 gap-2">
          <div className="text-[12px] text-sd-mid-gray">{jd.length} characters</div>
          <div className="flex gap-2">
            {result && (
              <>
                <button
                  className="sd-btn-secondary inline-flex items-center gap-2"
                  onClick={() => setCoverOpen(true)}
                  data-testid="jd-draft-cover-letter"
                  aria-label={`Draft a cover letter for ${result.jd_title || 'this role'}`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                  Draft cover letter
                </button>
                <button className="sd-btn-secondary" onClick={startNewJD} data-testid="jd-new">+ New JD</button>
              </>
            )}
            <button className="sd-btn-primary px-7" onClick={submitJD} disabled={busy} data-testid="jd-analyze">
              {pendingJdJob ? (
                <span className="inline-flex items-center gap-2"><Spinner size={16} color="#FFFFFF" /> Analyzing…</span>
              ) : openingId ? (
                <span className="inline-flex items-center gap-2"><Spinner size={16} color="#FFFFFF" /> Loading…</span>
              ) : (
                'Analyze'
              )}
            </button>
          </div>
        </div>
        {pendingJdJob && (
          <JdAnalysisProgress startedAt={pendingJdJob.started_at} />
        )}
        {!pendingJdJob && finishing && (
          <JdAnalysisProgress startedAt={lastJobStartRef.current || Date.now()} done />
        )}
        {error && (
          <div className="mt-3 rounded-card border-l-4 border-sd-error bg-[#FDF2F2] text-[#76313B] p-3 text-[13px]" data-testid="jd-error">{error}</div>
        )}
      </div>

      {result && (
        <div className="animate-fade-in">
          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
            <div className="text-[11px] uppercase tracking-[0.14em] text-sd-mid-gray font-semibold">
              {result.jd_title || 'Job description'} {result.jd_company ? `· ${result.jd_company}` : ''}
            </div>
            {currentJdId && (
              <button
                type="button"
                onClick={() => {
                  try { window.open(`/dashboard/jd-report/${currentJdId}?print=1`, '_blank', 'noopener'); } catch {}
                }}
                className="sd-btn-secondary inline-flex items-center gap-2 !py-1.5 !px-3 !text-[12.5px]"
                data-testid="jd-export-single"
                title="Export this JD analysis as PDF"
                aria-label="Export this JD analysis as PDF"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
                  <polyline points="16 6 12 2 8 6" />
                  <line x1="12" y1="2" x2="12" y2="15" />
                </svg>
                Export this analysis
              </button>
            )}
          </div>
          <div className="flex gap-2 mb-5 border-b border-sd-light-gray overflow-x-auto">
            {['You vs the JD', 'CV Audit', 'Interview Prep', 'Optimize LinkedIn'].map((label, idx) => (
              <button key={idx} className={`px-4 py-2.5 text-[13.5px] font-medium border-b-2 transition-colors whitespace-nowrap ${tab === idx ? 'text-sd-black border-sd-primary' : 'text-sd-mid-gray border-transparent hover:text-sd-black'}`} onClick={() => setTab(idx)} data-testid={`jd-tab-${idx}`}>
                {label}
              </button>
            ))}
          </div>
          {tab === 0 && <TabFit r={result} progress={progress} toggleJdAction={toggleJdAction} onDraftCoverLetter={() => setCoverOpen(true)} />}
          {tab === 1 && <TabCvAudit r={result} jdId={currentJdId} onDraftCoverLetter={() => setCoverOpen(true)} onResultUpdated={(newResult) => setResult(newResult)} />}
          {tab === 2 && <TabInterview r={result} />}
          {tab === 3 && <TabLinkedIn r={result} jdId={currentJdId} onResultUpdated={(newResult) => setResult(newResult)} />}
        </div>
      )}

      {!result && !busy && (
        <JDAnalyzerPreview />
      )}

      {coverOpen && jdRole && (
        <CoverLetterModal role={jdRole} onClose={() => setCoverOpen(false)} />
      )}
    </div>
  );
}

/* ---------------- Determinate JD analysis progress bar ----------------
   No real backend progress events exist, so the bar runs on a smoothed
   timer calibrated to ~60s that eases toward 90% and HOLDS there until the
   response lands, then jumps to 100%. Monotonic (driven by elapsed time) —
   it can never freeze at 100% early or move backwards. */
const JD_STAGES = [
  { at: 0, label: 'Parsing job description' },
  { at: 20, label: 'Extracting required skills' },
  { at: 45, label: 'Mapping against your profile' },
  { at: 70, label: 'Scoring alignment' },
  { at: 88, label: 'Finalizing analysis' },
];

function JdAnalysisProgress({ startedAt, done = false }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 300);
    return () => clearInterval(iv);
  }, []);
  const elapsedSec = Math.max(0, Math.floor((now - (startedAt || now)) / 1000));
  const pct = done ? 100 : Math.min(90, Math.round(90 * (1 - Math.exp(-elapsedSec / 24))));
  const stage = done
    ? 'Analysis complete'
    : [...JD_STAGES].reverse().find((s) => pct >= s.at)?.label || JD_STAGES[0].label;
  const mm = Math.floor(elapsedSec / 60);
  const ss = String(elapsedSec % 60).padStart(2, '0');
  return (
    <div className="mt-3 rounded-card border border-[#4CD964]/30 bg-[#F0F8F3] p-4" data-testid="jd-progress-panel">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <span className="text-[13px] font-semibold text-[#1F4D2A]" data-testid="jd-progress-stage">{stage}</span>
        <span className="text-[12px] font-mono text-[#1F4D2A] tabular-nums">
          <span data-testid="jd-progress-pct">{pct}%</span> · {mm}:{ss} elapsed
        </span>
      </div>
      <div className="h-2 rounded-full bg-white overflow-hidden border border-[#4CD964]/25">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[#3AA856] to-[#4CD964]"
          style={{ width: `${pct}%`, transition: 'width 600ms cubic-bezier(.25,.8,.25,1)' }}
          data-testid="jd-progress-bar"
        />
      </div>
      <div className="text-[12px] text-[#1F4D2A]/80 mt-2" data-testid="jd-progress-note">
        {done
          ? 'Opening your results…'
          : 'Runs in the background — feel free to switch tabs. Longer analyses can take a few minutes; we won\u2019t give up before 5 minutes.'}
      </div>
    </div>
  );
}

/* ---------------- JD Analyzer PREVIEW (empty-state demo) ---------------- */

/**
 * A four-tab cycling demo that shows what the JD Analyzer produces, using the
 * same Meridian Group mock profile as the AnalysisLoading demo. Wrapped in a
 * dark "device frame" so it clearly reads as a PREVIEW (not real data). Each
 * tab includes subtle "flip / reveal" micro-animations so viewers see there
 * IS content underneath — chips, bullets, scores flip in and out on cycle.
 */
const JD_DEMO_TABS = [
  {
    id: 'fit',
    label: 'Skills you match',
    caption: 'Realistic score against a live posting — no vanity numbers',
  },
  {
    id: 'audit',
    label: 'CV rewrites',
    caption: 'Bullet-by-bullet rewrites the JD Analyzer proposes',
  },
  {
    id: 'interview',
    label: 'Interview prep',
    caption: 'Framework + drafted answer for the questions you\'ll get',
  },
  {
    id: 'linkedin',
    label: 'LinkedIn tune-up',
    caption: 'Headline + About rewrites tailored to the role',
  },
];

function JDAnalyzerPreview() {
  const [tab, setTab] = useState(0);
  // Two flip cadences: fast for the Skills tab (score digit teaser), slower
  // for the content-heavy tabs so the reader can actually parse each side.
  // 50% slower = 2200ms * 1.5 = 3300ms.
  const [flipTick, setFlipTick] = useState(0);        // fast, for Skills tab
  const [flipTickSlow, setFlipTickSlow] = useState(0); // 50% slower, for the others
  useEffect(() => {
    const iv = setInterval(() => setTab((i) => (i + 1) % JD_DEMO_TABS.length), 8500);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    const iv = setInterval(() => setFlipTick((n) => n + 1), 2200);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    const iv = setInterval(() => setFlipTickSlow((n) => n + 1), 3300);
    return () => clearInterval(iv);
  }, []);
  const active = JD_DEMO_TABS[tab];
  return (
    <div
      className="mt-6 rounded-[22px] overflow-hidden shadow-[0_18px_60px_-20px_rgba(0,0,0,0.55)] relative"
      style={{
        background: 'linear-gradient(180deg, #0F1917 0%, #101B18 55%, #0B1512 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
      data-testid="jd-analyzer-preview"
    >
      {/* Preview ribbon */}
      <div className="flex items-center justify-between px-5 pt-4 pb-2">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[#4CD964] shadow-[0_0_10px_rgba(76,217,100,0.6)]" />
          <span className="text-[10.5px] font-semibold tracking-[0.14em] uppercase text-[#4CD964]">Preview · demo data</span>
        </div>
        <span className="text-[10.5px] tracking-[0.06em] text-white/50">Your version will contain your data</span>
      </div>

      {/* Sample JD header */}
      <div className="px-5 pb-4 border-b border-white/10">
        <div className="text-[12.5px] text-white/70">
          Sample JD: <strong className="text-white">Director of Sustainability — Meridian Group</strong>
          <span className="text-white/40"> · London · £130K–£175K</span>
        </div>
      </div>

      {/* Tab strip */}
      <div className="flex border-b border-white/10 overflow-x-auto no-scrollbar" style={{ background: 'rgba(0,0,0,0.22)' }}>
        {JD_DEMO_TABS.map((t, i) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(i)}
            className={
              'shrink-0 px-4 py-3 text-[12.5px] font-medium transition-colors relative ' +
              (i === tab ? 'text-[#4CD964]' : 'text-white/50 hover:text-white')
            }
            data-testid={`jd-preview-tab-${t.id}`}
          >
            {t.label}
            {i === tab && (
              <span
                className="absolute bottom-0 left-3 right-3 h-[2px] bg-[#4CD964] rounded-full"
                style={{ boxShadow: '0 0 10px rgba(76,217,100,0.6)' }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Body — light content, matching the actual JD Analyzer view. The
          top ribbon + JD header + tab strip stay dark (preview chrome), and
          the content below reads as the real app rendered underneath — the
          two-tone split is what makes it clearly read as a preview. */}
      <div
        className="p-5 min-h-[340px] bg-sd-ivory"
      >
        <div className="text-[11px] uppercase tracking-[0.08em] text-sd-mid-gray font-semibold mb-3">{active.caption}</div>
        {active.id === 'fit' && <JDPreviewFit flipTick={flipTick} />}
        {active.id === 'audit' && <JDPreviewAudit flipTick={flipTickSlow} />}
        {active.id === 'interview' && <JDPreviewInterview flipTick={flipTickSlow} />}
        {active.id === 'linkedin' && <JDPreviewLinkedIn flipTick={flipTickSlow} />}
      </div>
    </div>
  );
}

/* Small helper: 3D flip card for a single teaser chip / cell. Front + back
   swap based on a boolean derived from flipTick. Both faces share the same
   CSS-grid cell so the wrapper takes the height of the TALLER face — this
   fixes the chip-row overlap on the CV rewrites tab where the "after" text
   is significantly longer than the "before" line. */
function PreviewFlip({ front, back, delay = 0, flipTick = 0 }) {
  // Offset the flip cadence per card so the whole grid doesn't move in lockstep.
  const isBack = ((flipTick + delay) % 2) === 1;
  return (
    <div className="relative" style={{ perspective: 900 }}>
      <div
        className="transition-transform"
        style={{
          transformStyle: 'preserve-3d',
          transform: isBack ? 'rotateY(180deg)' : 'rotateY(0deg)',
          transitionDuration: '650ms',
          transitionTimingFunction: 'cubic-bezier(0.4, 0.15, 0.2, 1)',
          display: 'grid',
        }}
      >
        <div style={{ gridArea: '1 / 1', backfaceVisibility: 'hidden' }}>
          {front}
        </div>
        <div
          style={{
            gridArea: '1 / 1',
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          {back}
        </div>
      </div>
    </div>
  );
}

function JDPreviewFit({ flipTick }) {
  const reqs = [
    { label: 'CSRD & ESRS end-to-end delivery', status: 'strong' },
    { label: 'Scope 3 across a listed group', status: 'strong' },
    { label: 'P&L ownership on a sustainability line', status: 'gap' },
    { label: 'Board-level reporting cadence', status: 'partial' },
    { label: 'SBTi FLAG / land-use pathway', status: 'missing' },
  ];
  return (
    <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-4 items-start">
      <div className="min-w-[110px]">
        <PreviewFlip
          flipTick={flipTick}
          delay={0}
          front={(
            <div>
              <div className="text-[52px] font-semibold text-sd-primary leading-none tabular-nums">78</div>
              <div className="text-[11px] uppercase tracking-[0.09em] font-semibold text-sd-primary mt-1">Match score</div>
              <div className="text-[11.5px] text-sd-mid-gray mt-1.5">Promising fit</div>
            </div>
          )}
          back={(
            <div>
              <div className="text-[52px] font-semibold text-sd-black leading-none tabular-nums">6/9</div>
              <div className="text-[11px] uppercase tracking-[0.09em] font-semibold text-sd-dark-gray mt-1">Requirements met</div>
              <div className="text-[11.5px] text-sd-mid-gray mt-1.5">3 gaps to close</div>
            </div>
          )}
        />
      </div>
      <ul className="space-y-1.5">
        {reqs.map((r, i) => {
          const meta = r.status === 'strong'
            ? { c: '#1F7A3A', bg: '#E9FAEC', label: 'STRONG' }
            : r.status === 'partial'
              ? { c: '#8A5A00', bg: '#FFF3D6', label: 'PARTIAL' }
              : r.status === 'gap'
                ? { c: '#8B2E2E', bg: '#FBE4E4', label: 'GAP' }
                : { c: '#6B6B6B', bg: '#EDEDED', label: 'MISSING' };
          return (
            <li key={i} className="flex items-center gap-3 text-[13px]">
              <span
                className="text-[9.5px] font-semibold uppercase tracking-[0.08em] px-1.5 py-0.5 rounded"
                style={{ color: meta.c, background: meta.bg }}
              >
                {meta.label}
              </span>
              <span className="text-sd-dark-gray">{r.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function JDPreviewAudit({ flipTick }) {
  return (
    <div className="space-y-4">
      <PreviewFlip
        flipTick={flipTick}
        delay={0}
        front={(
          <div className="rounded-[12px] border border-sd-light-gray bg-white p-3.5">
            <div className="text-[10.5px] uppercase tracking-[0.08em] text-sd-mid-gray font-semibold mb-1.5">Your bullet</div>
            <div className="text-[13px] text-sd-mid-gray leading-relaxed line-through">Led CSRD readiness at Meridian Group.</div>
          </div>
        )}
        back={(
          <div className="rounded-[12px] border border-sd-primary/40 bg-[#E9FAEC] p-3.5">
            <div className="text-[10.5px] uppercase tracking-[0.08em] text-sd-primary font-semibold mb-1.5">Rewrite · ATS-tuned</div>
            <div className="text-[13px] leading-relaxed font-medium" style={{ color: '#1F4D2A' }}>
              Delivered CSRD Wave 1 disclosure for Meridian Group's £2B consumer portfolio — 40 category-A suppliers mapped, EFRAG-aligned audit trail, first-year unqualified assurance opinion.
            </div>
          </div>
        )}
      />
      {/* Keyword row sits cleanly BELOW the rewrite card. */}
      <div className="rounded-[10px] bg-white border border-sd-light-gray px-3 py-2 flex flex-wrap gap-x-3 gap-y-1 text-[11.5px] text-sd-primary font-medium">
        <span>+ CSRD Wave 1</span>
        <span>+ £2B portfolio</span>
        <span>+ 40 suppliers</span>
        <span>+ EFRAG</span>
        <span>+ unqualified assurance</span>
      </div>
      <div className="text-[11.5px] text-sd-mid-gray">4 more bullets rewritten · 12 ATS keywords added · 3 quantified metrics inserted.</div>
    </div>
  );
}

/* Interview preview — mirrors the real Interview Prep view: a set of question
   types visible at once, ONE expanded by default (Behavioral), and the
   expanded card FLIPS between the question setup and the drafted STAR
   answer with yellow placeholder highlights — the same feature the live
   JD Analyzer uses (DraftAnswerBlock). No user interaction required; the
   flip and expand are both static-demo behaviours driven by flipTick. */
function JDPreviewInterview({ flipTick }) {
  const rows = [
    {
      type: 'Technical',
      accent: '#2E7CD1',
      accentBg: '#E3EEFB',
      q: '"Walk me through how you built the ESRS data model for your CSRD readiness cycle."',
    },
    {
      type: 'Strategic',
      accent: '#8A5A00',
      accentBg: '#FFF3D6',
      q: '"How would you structure the sustainability P&L line so the CFO can defend it to analysts?"',
    },
    {
      type: 'Behavioral',
      accent: '#1F7A3A',
      accentBg: '#E9FAEC',
      q: '"Tell me about a time you delivered a sustainability report that survived audit scrutiny."',
      expanded: true,
    },
  ];

  // Behavioral drafted answer — STAR-structured, with yellow placeholder
  // pills exactly as the real DraftAnswerBlock feature renders them.
  const starAnswer = (
    <div className="space-y-2 text-[12.5px] leading-relaxed" style={{ color: '#1F4D2A' }}>
      <div>
        <span className="font-semibold text-sd-primary mr-1.5">Situation.</span>
        At Meridian Group my team owned the FY24 CSRD readiness cycle for a £2B consumer portfolio spanning
        {' '}
        <PlaceholderPill>[MARKETS — e.g. 12 markets]</PlaceholderPill>
        {' '}and
        {' '}
        <PlaceholderPill>[SUPPLIER SCOPE — e.g. 40 category-A suppliers]</PlaceholderPill>.
      </div>
      <div>
        <span className="font-semibold text-sd-primary mr-1.5">Task.</span>
        Deliver a first-year
        {' '}
        <PlaceholderPill>[AUDIT OPINION LEVEL — e.g. unqualified]</PlaceholderPill>
        {' '}assurance opinion under Wave 1 while the underlying data owners still sat outside sustainability.
      </div>
      <div>
        <span className="font-semibold text-sd-primary mr-1.5">Action.</span>
        Stood up an EFRAG-aligned data model jointly with Finance and Risk, ran
        {' '}
        <PlaceholderPill>[DRY-RUN COUNT — e.g. three internal dry-runs]</PlaceholderPill>
        {' '}with the audit partner, and closed the last
        {' '}
        <PlaceholderPill>[GAP DETAIL — e.g. 12 supplier records]</PlaceholderPill>
        {' '}through a joint procurement task-force.
      </div>
      <div>
        <span className="font-semibold text-sd-primary mr-1.5">Result.</span>
        <PlaceholderPill>[OUTCOME — e.g. clean opinion with 2 immaterial observations]</PlaceholderPill>. The data-lineage playbook we built is now the group standard across all business units.
      </div>
    </div>
  );

  const questionFront = (
    <div className="rounded-[10px] bg-white border border-sd-light-gray p-3">
      <div className="text-[10.5px] uppercase tracking-[0.09em] text-sd-mid-gray font-semibold mb-1.5">Draft answer preview · click card to flip</div>
      <ul className="text-[12.5px] text-sd-dark-gray space-y-1">
        <li>· Situation — where were you, what was the scope</li>
        <li>· Task — what were you accountable for</li>
        <li>· Action — the moves you personally made</li>
        <li>· Result — the outcome, with a real metric</li>
      </ul>
      <div className="text-[11px] text-sd-mid-gray italic mt-2">Tap to reveal your drafted answer →</div>
    </div>
  );

  const answerBack = (
    <div className="rounded-[10px] border p-3" style={{ background: '#F5FBF7', borderColor: 'rgba(76,217,100,0.4)' }}>
      <div className="text-[10.5px] uppercase tracking-[0.09em] text-sd-primary font-semibold mb-2">Drafted answer · STAR · fill the yellow bits</div>
      {starAnswer}
    </div>
  );

  return (
    <div className="space-y-2.5">
      {rows.map((row) => (
        <div
          key={row.type}
          className="rounded-[12px] border border-sd-light-gray bg-white p-3.5"
          data-testid={`jd-preview-interview-row-${row.type.toLowerCase().replace(/\s+/g,'-')}`}
        >
          <div className="flex items-start gap-3">
            <span
              className="text-[9.5px] font-semibold uppercase tracking-[0.08em] px-1.5 py-0.5 rounded shrink-0 mt-0.5"
              style={{ color: row.accent, background: row.accentBg }}
            >
              {row.type}
            </span>
            <div className="flex-1 min-w-0">
              <div className={`text-[13px] leading-snug ${row.expanded ? 'text-sd-black font-medium' : 'text-sd-dark-gray'}`}>
                {row.q}
              </div>
              {row.expanded && (
                <div className="mt-3">
                  <PreviewFlip
                    flipTick={flipTick}
                    delay={0}
                    front={questionFront}
                    back={answerBack}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
      <div className="text-[11px] text-sd-mid-gray pt-1">Three question types · framework + drafted answer for each · pulled from the JD Analyzer's Interview Prep view.</div>
    </div>
  );
}

/* Yellow placeholder pill — same visual treatment as the real
   DraftAnswerBlock feature so the preview reads as a faithful sample. */
function PlaceholderPill({ children }) {
  return (
    <span
      className="px-1 py-0.5 rounded font-medium"
      style={{
        backgroundColor: '#FFF3D6',
        color: '#8A5A00',
        boxShadow: 'inset 0 0 0 1px #F5D77E',
      }}
    >
      {children}
    </span>
  );
}

function JDPreviewLinkedIn({ flipTick }) {
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <PreviewFlip
        flipTick={flipTick}
        delay={0}
        front={(
          <div className="rounded-[12px] border border-sd-light-gray bg-white p-4 min-h-[130px]">
            <div className="text-[10.5px] uppercase tracking-[0.08em] text-sd-mid-gray font-semibold mb-1.5">Headline · before</div>
            <div className="text-[13px] text-sd-mid-gray line-through">Head of Sustainability at Meridian Group</div>
          </div>
        )}
        back={(
          <div className="rounded-[12px] border border-sd-primary/40 bg-[#E9FAEC] p-4 min-h-[130px]">
            <div className="text-[10.5px] uppercase tracking-[0.08em] text-sd-primary font-semibold mb-1.5">Headline · after</div>
            <div className="text-[13px] font-medium leading-snug" style={{ color: '#1F4D2A' }}>
              Head of Sustainability · CSRD & Scope 3 across a £2B listed portfolio · Director-track
            </div>
          </div>
        )}
      />
      <PreviewFlip
        flipTick={flipTick}
        delay={1}
        front={(
          <div className="rounded-[12px] border border-sd-light-gray bg-white p-4 min-h-[130px]">
            <div className="text-[10.5px] uppercase tracking-[0.08em] text-sd-mid-gray font-semibold mb-1.5">About · current opener</div>
            <div className="text-[13px] text-sd-mid-gray line-through leading-relaxed">Passionate sustainability leader dedicated to making a real impact for the future.</div>
          </div>
        )}
        back={(
          <div className="rounded-[12px] border border-sd-primary/40 bg-[#E9FAEC] p-4 min-h-[130px]">
            <div className="text-[10.5px] uppercase tracking-[0.08em] text-sd-primary font-semibold mb-1.5">About · new opener</div>
            <div className="text-[13px] leading-relaxed" style={{ color: '#1F4D2A' }}>
              You run the reporting a Fortune 500 team runs — on a mid-cap band. Six years of CSRD readiness, Scope 3, and board-level narrative at Meridian.
            </div>
          </div>
        )}
      />
    </div>
  );
}

/* ---------------- JD result tabs ---------------- */

function TabFit({ r, progress, toggleJdAction, onDraftCoverLetter }) {
  const reqs = r.requirements || [];
  const matched = reqs.filter(x => x.status === 'strong').length;
  const total = reqs.length;

  // ---- CV Audit derivations -----------------------------------------------
  const score = Number(r.match_score) || 0;
  const wins = reqs
    .filter((x) => x.status === 'strong' || x.status === 'covered')
    .slice(0, 3);
  const gaps = reqs
    .filter((x) => x.status === 'gap' || x.status === 'missing' || (x.status === 'partial' && x.severity === 'dealbreaker'))
    .slice(0, 3);
  const verdict = score >= 80
    ? { label: 'Strong fit', tone: '#4CD964', bg: '#E9FAEC', message: 'Your CV lines up cleanly with what this role asks for. A tight cover letter and interview prep should carry you through.' }
    : score >= 60
      ? { label: 'Promising fit', tone: '#D4943A', bg: '#FFF4E0', message: 'You bring enough of the core to be a serious candidate. The gaps below are the ones the hiring manager will probe — prep answers for each.' }
      : { label: 'Stretch fit', tone: '#C94040', bg: '#FDF2F2', message: 'Meaningful gaps stand between you and this role. Applying is still worth it if the target is a growth stretch — go in with a plan for the misses below.' };
  return (
    <div className="space-y-5">
      {/* CV Audit hero — opinionated summary of how the CV lines up with THIS JD */}
      <section className="sd-card p-6" data-testid="jd-cv-audit">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[11px] uppercase tracking-[0.14em] text-sd-mid-gray font-semibold">CV Audit</span>
            {r.jd_title && (
              <span className="text-[12.5px] text-sd-mid-gray">
                · your CV vs <span className="text-sd-black font-semibold">{r.jd_title}</span>
                {r.jd_company ? <span className="text-sd-mid-gray"> at {r.jd_company}</span> : null}
              </span>
            )}
          </div>
          {onDraftCoverLetter && (
            <button
              type="button"
              onClick={onDraftCoverLetter}
              className="sd-btn-primary inline-flex items-center gap-2 !py-2 !px-3.5 !text-[13px]"
              data-testid="jd-cv-audit-cover-letter"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              Draft cover letter
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-5 md:gap-6 items-center mb-5">
          <div className="flex items-center gap-4">
            <div className="text-[46px] font-semibold text-sd-black leading-none tabular-nums" data-testid="jd-cv-audit-score">
              {score}
              <span className="text-[22px] text-sd-mid-gray font-normal">%</span>
            </div>
            <span
              className="px-2.5 py-1 rounded-full text-[11.5px] font-semibold uppercase tracking-[0.06em]"
              style={{ background: verdict.bg, color: verdict.tone }}
              data-testid="jd-cv-audit-verdict"
            >
              {verdict.label}
            </span>
          </div>
          <p className="text-[14px] text-sd-dark-gray leading-relaxed">
            {r.competitive_edge || verdict.message}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg border border-[rgba(76,217,100,0.28)] bg-[#F5FFF8] p-4" data-testid="jd-cv-audit-wins">
            <div className="text-[11px] uppercase tracking-[0.14em] text-sd-primary-dark font-semibold mb-2 flex items-center gap-1.5">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#4CD964" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              What lands · your strongest {wins.length} moves
            </div>
            {wins.length === 0 ? (
              <p className="text-[13px] text-sd-mid-gray italic">The requirements list didn't surface strong CV matches — see the coverage table below.</p>
            ) : (
              <ul className="space-y-1.5">
                {wins.map((w, i) => (
                  <li key={i} className="text-[13.5px] text-sd-black leading-snug">
                    <span className="font-semibold">{w.req}</span>
                    {w.evidence && <span className="text-sd-mid-gray"> — {w.evidence}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-lg border border-[rgba(201,64,64,0.24)] bg-[#FFF5F5] p-4" data-testid="jd-cv-audit-gaps">
            <div className="text-[11px] uppercase tracking-[0.14em] text-sd-error font-semibold mb-2 flex items-center gap-1.5">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#C94040" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              What to close · your top {gaps.length} gap{gaps.length === 1 ? '' : 's'}
            </div>
            {gaps.length === 0 ? (
              <p className="text-[13px] text-sd-mid-gray italic">No critical gaps flagged — you cover the essentials.</p>
            ) : (
              <ul className="space-y-1.5">
                {gaps.map((g, i) => (
                  <li key={i} className="text-[13.5px] text-sd-black leading-snug">
                    <span className="font-semibold">{g.req}</span>
                    {g.action && <span className="text-sd-mid-gray"> — {g.action}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      <section className="sd-card p-6">
        <p className="text-[12px] uppercase tracking-[0.14em] text-sd-mid-gray font-medium mb-2">Requirements coverage · {r.jd_title} {r.jd_company ? `· ${r.jd_company}` : ''}</p>
        <h2 className="text-[22px] font-semibold text-sd-black mb-2">{matched} of {total} core requirements matched</h2>
        <div className="mt-3 flex items-center gap-4">
          <div className="flex-1 h-2 rounded-full bg-sd-light-gray overflow-hidden"><div className="h-full bg-sd-primary transition-all" style={{ width: `${r.match_score}%` }} /></div>
          <div className="text-[15px] font-semibold text-sd-primary font-mono">{r.match_score}%</div>
        </div>
      </section>
      <ReqSection title="Strongly matched" items={reqs.filter(x => x.status === 'strong')} variant="strong" defaultOpen={false} />
      <ReqSection title="Partially covered" items={reqs.filter(x => x.status === 'partial')} variant="partial" defaultOpen={true} />
      <ReqSection title="Not covered" items={reqs.filter(x => x.status === 'gap')} variant="gap" defaultOpen={true} dealbreakerOpen />
      <div className="mt-2 sd-card p-5">
        <h3 className="text-[15px] font-semibold text-sd-black mb-1">Save these actions to your plan</h3>
        <p className="text-sd-mid-gray text-[12.5px] mb-3">Tap the + to add an action to your plan. You'll tick them off in My Progress as you complete them.</p>
        <div className="space-y-2">
          {(r.saveable_actions || []).map((a, i) => {
            const key = `jd_${a.title}`;
            const saved = progress?.saved_jd_actions?.includes(key);
            return (
              <div key={i} className="flex items-center justify-between p-3 rounded-[10px] bg-sd-ivory">
                <div className="flex items-center gap-3 min-w-0">
                  <button
                    onClick={() => toggleJdAction(key)}
                    className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-[16px] font-bold transition-colors ${saved ? 'bg-sd-primary text-white' : 'bg-sd-light-gray text-sd-mid-gray hover:bg-sd-cream'}`}
                    data-testid={`jd-save-${i}`}
                    aria-pressed={saved}
                    title={saved ? 'Remove from plan' : 'Add to plan'}
                  >
                    {saved ? '−' : '+'}
                  </button>
                  <div className="min-w-0">
                    <div className="text-[14px] font-medium text-sd-black truncate">{a.title}</div>
                    <div className="text-[11.5px] text-sd-mid-gray">
                      {a.time}
                      {saved && <span className="ml-2 text-sd-primary font-semibold">· In your plan</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  {a.critical && <span className="text-[10px] px-2 py-0.5 rounded-pill bg-[#FBE4E4] text-[#C94040] uppercase tracking-wider font-semibold">Critical</span>}
                  <span className="text-[10px] px-2 py-0.5 rounded-pill bg-sd-cream text-sd-mid-gray uppercase tracking-wider font-semibold">{a.cost}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ReqSection({ title, items, variant, defaultOpen = true, dealbreakerOpen }) {
  // `variant` values:
  //   'strong'  → green MATCHED badge, expandable body shows evidence
  //   'partial' → amber/red severity badge, expandable body shows evidence + how to close
  //   'gap'     → same as partial but for missing requirements
  // All three sections now share the same section-header collapse control
  // (previously only "Strongly matched" had one).
  const [open, setOpen] = useState(defaultOpen);
  if (!items.length) return null;
  return (
    <div className="mb-5">
      <button
        className="flex items-center gap-2 text-[14px] font-semibold text-sd-black mb-2"
        onClick={() => setOpen(o => !o)}
        data-testid={`req-section-toggle-${variant}`}
      >
        <span className="text-sd-mid-gray w-3 inline-block text-center">{open ? '−' : '+'}</span>
        {title}
        <span className="text-sd-mid-gray font-normal">· {items.length}</span>
      </button>
      {open && (
        <div className="space-y-2 animate-fade-in">
          {items.map((x, i) => (
            <ReqCard
              key={i}
              x={x}
              variant={variant}
              defaultOpen={dealbreakerOpen && x.severity === 'dealbreaker'}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ReqCard({ x, variant, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const isStrong = variant === 'strong';
  const sev = x.severity || 'important';
  const badge = isStrong
    ? { bg: '#E9FAEC', color: '#1F4D2A', label: 'Matched' }
    : sev === 'dealbreaker'
      ? { bg: '#FBE4E4', color: '#C94040', label: 'Dealbreaker' }
      : sev === 'important'
        ? { bg: '#FBF1DE', color: '#7A5B16', label: 'Important' }
        : { bg: '#E3E5E3', color: '#7A7F7A', label: sev };
  return (
    <div className="sd-card p-4" data-testid={`req-card-${variant}`}>
      <button className="w-full flex items-start justify-between gap-3 text-left" onClick={() => setOpen(o => !o)}>
        <div className="flex items-start gap-2.5 flex-1 min-w-0">
          {isStrong && (
            <span className="text-sd-primary mt-0.5 shrink-0" aria-hidden>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </span>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-medium text-sd-black">{x.req}</div>
            {!isStrong && x.gap_detail && (
              <div className="text-[12.5px] text-sd-mid-gray mt-0.5">{x.gap_detail}</div>
            )}
          </div>
        </div>
        <span
          className="text-[10px] px-2 py-0.5 rounded-pill font-semibold uppercase tracking-wider shrink-0"
          style={{ background: badge.bg, color: badge.color }}
        >
          {badge.label}
        </span>
      </button>
      {open && (
        <div className="mt-3 p-3 rounded-[10px] bg-sd-ivory text-[13px] text-sd-dark-gray leading-relaxed animate-fade-in">
          {x.evidence && (
            <div className={x.action ? 'mb-2' : ''}>
              <strong className="text-sd-black">
                {isStrong ? 'What you bring:' : 'Your evidence:'}
              </strong>{' '}
              {x.evidence}
            </div>
          )}
          {!isStrong && x.action && (
            <div>
              <strong className="text-sd-black">How to close:</strong> {x.action}
            </div>
          )}
          {!x.evidence && !x.action && (
            <div className="text-sd-mid-gray italic">No additional notes for this requirement.</div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- CV Audit tab ---------------- */
// Deep-dive audit of the user's CV against this specific JD. Three modules:
//   1. Bullet rewrites — take a real CV bullet and rewrite it to speak the JD's language
//   2. ATS keyword coverage — show which JD terms the CV already contains vs missing
//   3. Quantified impact — flag bullets lacking numbers and suggest what to quantify
// All content is JD-linked, so the tab shows an empty state when no cv_audit
// data is present (graceful degradation for older analyses saved before v2).

function TabCvAudit({ r, jdId, onDraftCoverLetter, onResultUpdated }) {
  const audit = r?.cv_audit || {};
  const rewrites = Array.isArray(audit.bullet_rewrites) ? audit.bullet_rewrites : [];
  const ats = audit.ats_keywords || { present: [], missing: [] };
  const present = Array.isArray(ats.present) ? ats.present : [];
  const missing = Array.isArray(ats.missing) ? ats.missing : [];
  const quantified = Array.isArray(audit.quantified_impact) ? audit.quantified_impact : [];

  const hasAny = rewrites.length > 0 || present.length > 0 || missing.length > 0 || quantified.length > 0;

  // Sort missing keywords: critical > important > nice-to-have, so the user
  // eyeballs the most consequential ones first.
  const priorityOrder = { critical: 0, important: 1, 'nice-to-have': 2 };
  const missingSorted = [...missing].sort(
    (a, b) => (priorityOrder[a.priority] ?? 3) - (priorityOrder[b.priority] ?? 3),
  );

  // ------------- CV upload widget state (empty-state branch) --------------
  const [cvFile, setCvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  // Elapsed seconds since the current audit job kicked off — used for the
  // "still running" copy so users don't feel stranded past ~30s.
  const [elapsed, setElapsed] = useState(0);

  // ------------- Resume-on-mount --------------------------------------------
  // If the user kicked off a CV audit, navigated away, and came back, we look
  // up the most recent in-flight cv_audit job for this JD and re-attach the
  // polling loop. This is the whole point of Fix 7 — audits are server-side
  // and no longer die when the tab loses focus.
  useEffect(() => {
    if (!jdId || hasAny) return;  // nothing to resume if the audit already landed
    let cancelled = false;
    (async () => {
      try {
        const active = await analysisApi.jdCvAuditActive(jdId);
        if (cancelled || !active?.job_id) return;
        setUploading(true);
        setElapsed(active.elapsed_seconds || 0);
        try {
          const done = await pollJob(active.job_id, {
            intervalMs: 2500,
            timeoutMs: 15 * 60 * 1000,
            onProgress: (j) => setElapsed(j.elapsed_seconds || 0),
          });
          if (cancelled) return;
          const payload = done.result || {};
          if (onResultUpdated) {
            onResultUpdated(payload.result || { ...r, cv_audit: payload.cv_audit });
          }
        } catch (err) {
          if (!cancelled) {
            setUploadError(err?.message || 'CV Audit failed. Please try again.');
          }
        } finally {
          if (!cancelled) setUploading(false);
        }
      } catch {
        // Silent — no active job to resume, that's fine.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jdId, hasAny]);

  const runUpload = async () => {
    if (!cvFile || !jdId) return;
    setUploading(true); setUploadError(null); setElapsed(0);
    try {
      // Kick off the audit as a background job — returns immediately.
      const { job_id } = await analysisApi.jdCvAuditUploadAsync(jdId, cvFile);
      // Poll until done. pollJob already handles tab-backgrounding and
      // transient network hiccups; the server-side job survives even if
      // we lose the frontend entirely.
      const done = await pollJob(job_id, {
        intervalMs: 2500,
        timeoutMs: 15 * 60 * 1000,
        onProgress: (j) => setElapsed(j.elapsed_seconds || 0),
      });
      const payload = done.result || {};
      if (onResultUpdated) {
        onResultUpdated(payload.result || { ...r, cv_audit: payload.cv_audit });
      }
    } catch (e) {
      setUploadError(e?.response?.data?.detail || e?.message || 'CV Audit generation failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (!hasAny) {
    // While uploading, show a dedicated full-tab analyzing state so the user
    // has clear visual feedback for the ~20s wait. Otherwise show the upload
    // widget.
    if (uploading) {
      const elapsedLabel = elapsed > 0
        ? `Running for ${elapsed}s — this usually finishes in 15–25 seconds.`
        : 'This usually takes 15–25 seconds.';
      return (
        <div className="sd-card p-8 flex flex-col items-center justify-center text-center" data-testid="cv-audit-analyzing">
          <div className="w-16 h-16 rounded-full bg-sd-ivory flex items-center justify-center mb-4">
            <Spinner size={30} color="#4CD964" />
          </div>
          <h2 className="text-[17px] font-semibold text-sd-black mb-1">Analyzing your CV against this JD…</h2>
          <p className="text-[13.5px] text-sd-mid-gray max-w-[440px] leading-relaxed">
            Reading your CV, cross-checking every requirement, drafting rewrite suggestions, and finding the ATS keywords worth adding. {elapsedLabel}
          </p>
          <div className="mt-5 w-full max-w-[280px] h-1.5 rounded-full bg-sd-light-gray overflow-hidden">
            <div className="h-full rounded-full bg-sd-primary sd-progress-indeterminate" />
          </div>
          <p className="text-[11.5px] text-sd-mid-gray mt-4 italic max-w-[440px] leading-relaxed">
            Feel free to switch tabs or close this window — the audit keeps running on our servers and will be here when you come back.
          </p>
        </div>
      );
    }
    return (
      <div className="space-y-4" data-testid="cv-audit-empty-uploader">
        <div className="sd-card p-6 bg-sd-ivory">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shrink-0">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4CD964" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-[16px] font-semibold text-sd-black mb-1">Upload your CV to unlock CV Audit</h2>
              <p className="text-[13px] text-sd-mid-gray leading-relaxed">
                CV Audit compares your CV line-by-line against <strong>{r.jd_title || 'this JD'}</strong> — bullet rewrites, missing ATS keywords, and where to add metrics. Upload a .pdf, .docx, or .txt and we'll run the audit in about 20 seconds — no need to re-analyze the whole JD.
              </p>
            </div>
          </div>
        </div>

        <label
          htmlFor="cv-audit-file-input"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault(); setDragOver(false);
            const f = e.dataTransfer.files?.[0];
            if (f) setCvFile(f);
          }}
          className={`sd-card p-6 flex flex-col items-center justify-center cursor-pointer transition-colors border-2 border-dashed ${dragOver ? 'border-sd-primary bg-[#F5FFF8]' : 'border-sd-light-gray hover:border-sd-primary hover:bg-[#FAFBFA]'}`}
          data-testid="cv-audit-dropzone"
        >
          <input
            id="cv-audit-file-input"
            type="file"
            accept=".pdf,.docx,.txt"
            className="hidden"
            onChange={(e) => setCvFile(e.target.files?.[0] || null)}
            data-testid="cv-audit-file-input"
          />
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#4CD964" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          {cvFile ? (
            <>
              <div className="mt-3 text-[14px] font-semibold text-sd-black" data-testid="cv-audit-file-name">{cvFile.name}</div>
              <div className="text-[12px] text-sd-mid-gray mt-0.5">{Math.round(cvFile.size / 1024)} KB · click to swap file</div>
            </>
          ) : (
            <>
              <div className="mt-3 text-[14px] font-semibold text-sd-black">Drop your CV here, or click to browse</div>
              <div className="text-[12px] text-sd-mid-gray mt-0.5">PDF, DOCX, or TXT · under 5 MB</div>
            </>
          )}
        </label>

        <div className="flex items-center justify-end gap-2">
          {uploadError && <div className="text-[12.5px] text-sd-error flex-1" data-testid="cv-audit-upload-error">{uploadError}</div>}
          <button
            type="button"
            onClick={runUpload}
            disabled={!cvFile || uploading || !jdId}
            className="sd-btn-primary inline-flex items-center gap-2"
            data-testid="cv-audit-run-btn"
          >
            {uploading ? <><Spinner size={13} color="#FFFFFF" /> Running CV Audit…</> : 'Run CV Audit against this JD'}
          </button>
        </div>

        {!jdId && (
          <div className="text-[12px] text-sd-mid-gray italic">
            Save this analysis first before uploading a CV.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cv-audit-tab">
      {/* HEADER */}
      <section className="sd-card p-6" data-testid="cv-audit-header">
        <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[11px] uppercase tracking-[0.14em] text-sd-mid-gray font-semibold">CV Audit</span>
            {r.jd_title && (
              <span className="text-[12.5px] text-sd-mid-gray">
                · your CV vs <span className="text-sd-black font-semibold">{r.jd_title}</span>
                {r.jd_company ? <span className="text-sd-mid-gray"> at {r.jd_company}</span> : null}
              </span>
            )}
          </div>
          {onDraftCoverLetter && (
            <button
              type="button"
              onClick={onDraftCoverLetter}
              className="sd-btn-primary inline-flex items-center gap-2 !py-2 !px-3.5 !text-[13px]"
              data-testid="cv-audit-cover-letter-btn"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              Draft cover letter
            </button>
          )}
        </div>
        <h2 className="text-[20px] font-semibold text-sd-black leading-snug">Rewrite your CV to speak this role's language.</h2>
        <p className="text-[13.5px] text-sd-mid-gray leading-relaxed mt-1.5">
          Three targeted moves — line-by-line rewrites, missing ATS keywords, and where to add numbers. Everything below is grounded in <em>your</em> actual profile plus the JD you pasted.
        </p>
      </section>

      {/* BULLET REWRITES */}
      {rewrites.length > 0 && (
        <section data-testid="cv-audit-rewrites">
          <div className="flex items-baseline gap-2 mb-3">
            <h3 className="text-[16px] font-semibold text-sd-black">Bullet rewrites</h3>
            <span className="text-[12px] text-sd-mid-gray">— {rewrites.length} of your bullets, refocused for this JD</span>
          </div>
          <div className="space-y-4">
            {rewrites.map((b, i) => (
              <div key={i} className="sd-card p-5" data-testid={`cv-audit-rewrite-${i}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <div className="rounded-[10px] border border-sd-light-gray bg-sd-ivory p-3.5">
                    <div className="text-[10.5px] uppercase tracking-[0.14em] text-sd-mid-gray font-semibold mb-1.5 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-sd-mid-gray" aria-hidden />
                      Your current bullet
                    </div>
                    <p className="text-[13.5px] text-sd-dark-gray leading-relaxed">{b.original}</p>
                  </div>
                  <div className="rounded-[10px] border border-[rgba(76,217,100,0.32)] bg-[#F5FFF8] p-3.5">
                    <div className="text-[10.5px] uppercase tracking-[0.14em] text-sd-primary-dark font-semibold mb-1.5 flex items-center gap-1.5">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#1F4D2A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Suggested rewrite
                    </div>
                    <p className="text-[13.5px] text-sd-black leading-relaxed font-medium">{b.rewritten}</p>
                  </div>
                </div>
                {b.why && (
                  <div className="mt-3 flex items-start gap-2 text-[12.5px] text-sd-mid-gray leading-relaxed">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4CD964" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5" aria-hidden>
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4M12 8h.01" />
                    </svg>
                    <span><strong className="text-sd-black">Why:</strong> {b.why}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ATS KEYWORDS */}
      {(present.length > 0 || missingSorted.length > 0) && (
        <section data-testid="cv-audit-ats">
          <div className="flex items-baseline gap-2 mb-3">
            <h3 className="text-[16px] font-semibold text-sd-black">ATS keyword coverage</h3>
            <span className="text-[12px] text-sd-mid-gray">— what the applicant tracking system will filter on</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="sd-card p-5" data-testid="cv-audit-ats-present">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#E9FAEC]" aria-hidden>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1F4D2A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <h4 className="text-[14px] font-semibold text-sd-black">Present in your CV ({present.length})</h4>
              </div>
              {present.length === 0 ? (
                <p className="text-[13px] text-sd-mid-gray italic">The audit did not find any high-signal JD terms in your CV yet — see the missing list on the right.</p>
              ) : (
                <ul className="space-y-2">
                  {present.map((k, i) => (
                    <li key={i} className="flex items-baseline gap-2 text-[13px] leading-snug" data-testid={`cv-audit-ats-present-${i}`}>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-pill bg-[#E9FAEC] text-[#1F4D2A] text-[12px] font-semibold whitespace-nowrap">{k.term}</span>
                      {k.where && <span className="text-sd-mid-gray text-[12.5px]">— {k.where}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="sd-card p-5" data-testid="cv-audit-ats-missing">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#FBE4E4]" aria-hidden>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#C94040" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </span>
                <h4 className="text-[14px] font-semibold text-sd-black">Missing — add naturally ({missingSorted.length})</h4>
              </div>
              {missingSorted.length === 0 ? (
                <p className="text-[13px] text-sd-mid-gray italic">Nice — no critical JD keywords are missing from your CV.</p>
              ) : (
                <ul className="space-y-3">
                  {missingSorted.map((k, i) => {
                    const p = k.priority;
                    const chip = p === 'critical'
                      ? { bg: '#FBE4E4', color: '#C94040', label: 'Critical' }
                      : p === 'important'
                        ? { bg: '#FBF1DE', color: '#7A5B16', label: 'Important' }
                        : { bg: '#E3E5E3', color: '#5A6F67', label: 'Nice to have' };
                    return (
                      <li key={i} className="border-l-[3px] border-[rgba(201,64,64,0.36)] pl-3" data-testid={`cv-audit-ats-missing-${i}`}>
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-[13.5px] font-semibold text-sd-black">{k.term}</span>
                          <span
                            className="text-[10px] px-2 py-0.5 rounded-pill font-semibold uppercase tracking-wider"
                            style={{ background: chip.bg, color: chip.color }}
                          >
                            {chip.label}
                          </span>
                        </div>
                        {k.suggestion && (
                          <p className="text-[12.5px] text-sd-mid-gray leading-relaxed mt-1">{k.suggestion}</p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </section>
      )}

      {/* QUANTIFIED IMPACT */}
      {quantified.length > 0 && (
        <section data-testid="cv-audit-quantified">
          <div className="flex items-baseline gap-2 mb-3">
            <h3 className="text-[16px] font-semibold text-sd-black">Add numbers where they're missing</h3>
            <span className="text-[12px] text-sd-mid-gray">— {quantified.length} bullet{quantified.length === 1 ? '' : 's'} that would land harder with a metric</span>
          </div>
          <div className="space-y-3">
            {quantified.map((q, i) => (
              <div key={i} className="sd-card p-5" data-testid={`cv-audit-quantified-${i}`}>
                <div className="rounded-[10px] bg-sd-ivory p-3.5 mb-3">
                  <div className="text-[10.5px] uppercase tracking-[0.14em] text-sd-mid-gray font-semibold mb-1.5">Your bullet</div>
                  <p className="text-[13.5px] text-sd-dark-gray leading-relaxed">{q.bullet}</p>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#F0F8F3] shrink-0" aria-hidden>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1F4D2A" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="20" x2="12" y2="10" />
                      <line x1="18" y1="20" x2="18" y2="4" />
                      <line x1="6" y1="20" x2="6" y2="16" />
                    </svg>
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] text-sd-black leading-snug">
                      <strong className="text-sd-primary-dark">Add:</strong> {q.suggested_metric}
                    </div>
                    {q.why && (
                      <div className="text-[12.5px] text-sd-mid-gray leading-relaxed mt-1">{q.why}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}


function TabInterview({ r }) {
  const i = r.interview || {};
  const [tone, setTone] = useState('strategic');
  const lists = { strategic: i.strategic || [], technical: i.technical || [], behavioral: i.behavioral || [], ask_them: i.ask_them || [] };
  return (
    <div>
      <div className="mb-3">
        <h2 className="text-[18px] font-semibold text-sd-black">Match requirements to your experience</h2>
        <p className="text-sd-mid-gray text-[12.5px] mt-1">Tap a card to flip it — you'll see the framing that turns each requirement into a story from your own track record.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        {(r.positioning || []).map((p, idx) => (
          <FlipCard key={idx} height={170}
            front={(
              <div className="h-full flex flex-col justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-sd-mid-gray font-semibold">They ask about</div>
                  <div className="text-[16px] font-semibold text-sd-black mt-1.5 leading-snug">{p.they_say}</div>
                </div>
                <div className="flex items-center justify-between">
                  {p.critical && <span className="text-[10px] px-2 py-0.5 rounded-pill bg-[#FBE4E4] text-[#C94040] uppercase tracking-wider font-semibold">Key</span>}
                  <div className="text-[12px] text-sd-mid-gray">Click →</div>
                </div>
              </div>
            )}
            back={(
              <div className="h-full flex flex-col justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-wider opacity-90 font-semibold">You say</div>
                  <div className="text-[15px] font-semibold mt-1.5 leading-snug">{p.you_say}</div>
                </div>
                <div className="text-[11px] opacity-90 leading-snug">{p.translate}</div>
              </div>
            )}
          />
        ))}
      </div>
      {(r.gap_reframes || []).length > 0 && (
        <div className="sd-card p-5 mb-8">
          <h3 className="text-[15px] font-semibold text-sd-black mb-3">Reframe your gaps</h3>
          <div className="space-y-3">
            {r.gap_reframes.map((g, idx) => (
              <details key={idx} className="group">
                <summary className="cursor-pointer text-[13.5px] text-sd-dark-gray italic">“{g.gap}”</summary>
                <div className="mt-2 p-3 rounded-[10px] bg-[#E9FAEC] text-[13.5px] text-[#1F4D2A] leading-relaxed">{g.reframe}</div>
              </details>
            ))}
          </div>
        </div>
      )}
      <div className="mb-3">
        <h2 className="text-[18px] font-semibold text-sd-black">Practical preparation — how to answer their questions</h2>
        <p className="text-sd-mid-gray text-[12.5px] mt-1">Question banks by track. Expand any card for a structured answer framework tuned to this role.</p>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { id: 'strategic', label: 'Strategic', bg: '#4CD964' },
          { id: 'technical', label: 'Technical', bg: '#1F4D2A' },
          { id: 'behavioral', label: 'Behavioral', bg: '#C94040' },
          { id: 'ask_them', label: 'Ask them', bg: '#D4943A' },
        ].map(t => (
          <button key={t.id} className="sd-chip" style={tone === t.id ? { background: t.bg, color: '#FFFFFF' } : {}} onClick={() => setTone(t.id)} data-testid={`interview-tone-${t.id}`}>
            {t.label} · {lists[t.id].length}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {tone === 'behavioral' ? lists.behavioral.map((q, idx) => <BehaviorCard key={idx} q={q} />)
          : tone === 'ask_them' ? lists.ask_them.map((q, idx) => <AskCard key={idx} q={q} />)
          : lists[tone].map((q, idx) => <QuestionCard key={idx} q={q} tone={tone} />)}
      </div>
    </div>
  );
}

/**
 * Renders a full "drafted answer" for an interview question. Placeholders in
 * the form `[BRACKET — hint]` are highlighted so the user can spot exactly
 * where to plug in their own evidence. Below the draft we surface the LLM's
 * cv_hooks — short "your CV mentions X — plug it in here" nudges tied to each
 * placeholder token — so the user has zero-effort guidance on what to slot in.
 *
 * `alwaysShow` skips the "Show my draft answer" toggle and just renders the
 * draft inline (used for the "Ask them" cards where the whole draft is small).
 */
function DraftAnswerBlock({ draft, cvHooks, accent, idPrefix, alwaysShow = false }) {
  const [open, setOpen] = useState(alwaysShow);
  const [copied, setCopied] = useState(false);
  if (!draft) return null;

  // Split the draft into text + placeholder tokens so we can highlight them
  // inline. Matches `[ANYTHING NOT ] ]` including spaces, em-dashes, and
  // punctuation. The captured tokens keep their brackets in the render so
  // the user can search-and-replace easily.
  const parts = draft.split(/(\[[^\]]+\])/g);

  const copyToClipboard = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* clipboard blocked */ }
  };

  return (
    <div className="mt-4" data-testid={`${idPrefix}-draft-block`}>
      {!alwaysShow && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
          className="text-[12px] font-semibold uppercase tracking-[0.06em] hover:opacity-80 transition-opacity flex items-center gap-1.5"
          style={{ color: accent }}
          data-testid={`${idPrefix}-draft-toggle`}
        >
          {open ? 'Hide' : 'Show'} my draft answer
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={open ? 'rotate-180 transition-transform' : 'transition-transform'}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
      )}
      {open && (
        <div className="mt-2 rounded-[10px] border border-sd-light-gray bg-white p-3.5 animate-fade-in">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10.5px] font-semibold tracking-[0.08em] uppercase" style={{ color: accent }}>
              Your draft answer
            </div>
            <button
              type="button"
              onClick={copyToClipboard}
              className="text-[11px] font-semibold text-sd-mid-gray hover:text-sd-black flex items-center gap-1 transition-colors"
              data-testid={`${idPrefix}-draft-copy`}
            >
              {copied ? (
                <>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#4CD964" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <span className="text-sd-primary">Copied</span>
                </>
              ) : (
                <>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                  Copy
                </>
              )}
            </button>
          </div>
          <p className="text-[13.5px] text-sd-dark-gray leading-relaxed whitespace-pre-line">
            {parts.map((p, i) =>
              p.startsWith('[') && p.endsWith(']') ? (
                <mark
                  key={i}
                  className="px-1 py-0.5 rounded font-medium"
                  style={{ backgroundColor: '#FFF3D6', color: '#8A5A00', boxShadow: 'inset 0 0 0 1px #F5D77E' }}
                  data-testid={`${idPrefix}-placeholder`}
                >
                  {p}
                </mark>
              ) : (
                <span key={i}>{p}</span>
              )
            )}
          </p>
          {Array.isArray(cvHooks) && cvHooks.length > 0 && (
            <div className="mt-3 pt-3 border-t border-sd-light-gray/60">
              <div className="text-[10.5px] font-semibold tracking-[0.08em] uppercase text-sd-mid-gray mb-1.5">
                Where your CV plugs in
              </div>
              <ul className="space-y-1.5" data-testid={`${idPrefix}-cv-hooks`}>
                {cvHooks.map((h, i) => (
                  <li key={i} className="text-[12.5px] text-sd-dark-gray leading-snug flex items-start gap-2">
                    <span className="text-sd-primary shrink-0 mt-0.5">↳</span>
                    <span>
                      <code className="text-[11.5px] font-semibold text-sd-black bg-sd-cream px-1.5 py-0.5 rounded">
                        {h.placeholder}
                      </code>
                      <span className="ml-1.5">{h.hint}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function QuestionCard({ q, tone }) {
  const [open, setOpen] = useState(false);
  const accent = tone === 'strategic' ? '#4CD964' : '#1F4D2A';
  return (
    <div className="sd-card overflow-hidden">
      <button className="w-full p-4 text-left flex items-start justify-between gap-3 border-l-4" style={{ borderColor: accent }} onClick={() => setOpen(o => !o)}>
        <div className="flex-1">
          <div className="text-[14.5px] font-medium text-sd-black">{q.q}</div>
          {q.context && <div className="text-[12.5px] text-sd-mid-gray mt-1">{q.context}</div>}
        </div>
        <span className="text-sd-mid-gray">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 animate-fade-in border-l-4" style={{ borderColor: accent }}>
          {q.framework && <div className="mb-3"><span className="text-[11px] uppercase tracking-wider text-sd-mid-gray font-semibold">Framework</span><div className="text-[14px] font-medium text-sd-black">{q.framework}</div></div>}
          <ol className="space-y-2">
            {(q.steps || []).map((s, i) => (
              <li key={i} className="flex gap-3 text-[13.5px] text-sd-dark-gray">
                <span className="w-6 h-6 rounded-full bg-sd-cream text-sd-black text-[11.5px] font-semibold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                <div><strong className="text-sd-black">{s.label}:</strong> {s.detail}</div>
              </li>
            ))}
          </ol>
          {q.bridge && <div className="mt-3 p-3 rounded-[10px] bg-[#E9FAEC] text-[13.5px] text-[#1F4D2A] leading-relaxed"><strong>YOUR BRIDGE:</strong> {q.bridge}</div>}
          <DraftAnswerBlock draft={q.draft_answer} cvHooks={q.cv_hooks} accent={accent} idPrefix={`q-${tone}`} />
        </div>
      )}
    </div>
  );
}

function BehaviorCard({ q }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="sd-card p-4 border-l-4" style={{ borderColor: '#C94040' }}>
      <button className="w-full text-left" onClick={() => setOpen(o => !o)}>
        <div className="text-[14.5px] font-medium text-sd-black">{q.q}</div>
        {q.context && <div className="text-[12.5px] text-sd-mid-gray mt-1">{q.context}</div>}
      </button>
      {open && (
        <div className="mt-3 animate-fade-in">
          <div className="grid grid-cols-2 gap-2 mb-3">
            {['s', 't', 'a', 'r'].map(k => (
              <div key={k} className="p-3 rounded-[10px] bg-sd-ivory">
                <div className="text-[11px] uppercase tracking-wider text-[#C94040] font-semibold mb-1">{k.toUpperCase()}</div>
                <div className="text-[12.5px] text-sd-dark-gray leading-snug">{q.star?.[k] || '—'}</div>
              </div>
            ))}
          </div>
          {q.key_insight && <div className="p-3 rounded-[10px] bg-[#FBE4E4] text-[13px] text-[#76313B] leading-relaxed"><strong>KEY INSIGHT:</strong> {q.key_insight}</div>}
          <DraftAnswerBlock draft={q.draft_answer} cvHooks={q.cv_hooks} accent="#C94040" idPrefix="q-behavioral" />
        </div>
      )}
    </div>
  );
}

function AskCard({ q }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="sd-card p-4 border-l-4" style={{ borderColor: '#D4943A' }}>
      <div className="text-[14.5px] font-medium text-sd-black">{q.q}</div>
      <div className="mt-2 text-[12.5px] text-sd-mid-gray italic"><strong className="text-sd-black not-italic">Why ask:</strong> {q.why}</div>
      {q.draft_answer && (
        <>
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            className="mt-3 text-[12px] font-semibold uppercase tracking-[0.06em] text-[#D4943A] hover:text-[#B57923] transition-colors flex items-center gap-1.5"
            data-testid="ask-draft-toggle"
          >
            {open ? 'Hide' : 'Show'} the way you'd actually ask it
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={open ? 'rotate-180 transition-transform' : 'transition-transform'}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          {open && <DraftAnswerBlock draft={q.draft_answer} cvHooks={[]} accent="#D4943A" idPrefix="ask-draft" alwaysShow />}
        </>
      )}
    </div>
  );
}

function TabLinkedIn({ r, jdId, onResultUpdated }) {
  const l = r.linkedin || {};
  const [regenerating, setRegenerating] = useState(false);
  const [regenError, setRegenError] = useState(null);

  // A section is "empty" when the LLM returned neither current nor suggested
  // copy for it — this happened for a handful of early JD analyses and left
  // the UI showing a blank green pill. Now we show a clear fallback + a
  // one-click regenerate.
  const hasHeadline = !!(l.headline?.suggested || '').trim();
  const hasAbout = !!(l.about?.suggested || '').trim();
  const hasBullets = Array.isArray(l.bullets) && l.bullets.some(b => (b.after || '').trim());
  const hasAnyContent = hasHeadline || hasAbout || hasBullets;
  const hasAnyMissing = !hasHeadline || !hasAbout || !hasBullets;

  const runRegenerate = async () => {
    if (!jdId) return;
    setRegenerating(true); setRegenError(null);
    try {
      const data = await analysisApi.jdRegenerateLinkedIn(jdId);
      if (onResultUpdated) onResultUpdated(data.result || { ...r, linkedin: data.linkedin });
    } catch (e) {
      setRegenError(e?.response?.data?.detail || e?.message || 'Regeneration failed. Please try again.');
    } finally {
      setRegenerating(false);
    }
  };

  // Warning banner shown when the LinkedIn block came back partial or empty.
  // Shown when ANY of the three sub-sections is missing — not just when the
  // whole block is empty. Prompts an inline regenerate right at the top so
  // the user doesn't have to hunt for the button.
  const warningBanner = hasAnyMissing ? (
    <div className="sd-card p-5 mb-5 border-l-4 border-[#D4943A] bg-[#FFF7E6]" data-testid="linkedin-empty-warning">
      <div className="flex items-start gap-3">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8A5A00" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <div className="flex-1">
          <h3 className="text-[14.5px] font-semibold text-[#8A5A00] mb-1">
            {hasAnyContent
              ? "Some sections didn't come through"
              : "The LinkedIn rewrites didn't come through"}
          </h3>
          <p className="text-[13px] text-[#8A5A00]/90 mb-3">
            {hasAnyContent
              ? "The LLM truncated the LinkedIn block for this JD — "
              : "Sometimes the LLM truncates the LinkedIn block. "}
            {[
              !hasHeadline && 'Headline',
              !hasAbout && 'About section',
              !hasBullets && 'Experience bullets',
            ].filter(Boolean).join(', ') + (hasAnyContent ? ' still missing. ' : '. ')}
            Regenerate below — one focused Claude call, ~10 seconds, no need to redo the whole analysis.
          </p>
          <button
            type="button"
            onClick={runRegenerate}
            disabled={regenerating || !jdId}
            className="sd-btn-primary !py-1.5 !px-3 !text-[12.5px] inline-flex items-center gap-2"
            data-testid="linkedin-regenerate-full"
          >
            {regenerating ? <><Spinner size={12} color="#FFFFFF" /> Regenerating…</> : 'Regenerate LinkedIn section'}
          </button>
          {regenError && <div className="text-[12px] text-sd-error mt-2">{regenError}</div>}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div>
      {warningBanner}

      {hasAnyContent && (
        <div className="sd-card p-5 mb-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <h3 className="text-[14px] font-semibold text-sd-black">Why these rewrites work on LinkedIn</h3>
            {jdId && (
              <button
                type="button"
                onClick={runRegenerate}
                disabled={regenerating}
                className="text-[11.5px] font-semibold text-sd-mid-gray hover:text-sd-black flex items-center gap-1.5 transition-colors shrink-0"
                data-testid="linkedin-regenerate"
                title="Re-run just the LinkedIn block"
              >
                {regenerating ? <><Spinner size={11} color="#4A5568" /> Regenerating…</> : (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                    </svg>
                    Regenerate
                  </>
                )}
              </button>
            )}
          </div>
          <ul className="space-y-2 text-[13.5px] text-sd-dark-gray">
            <li className="flex gap-2"><span className="text-sd-primary">•</span> <span><strong>Recruiter search terms only:</strong> the new headline front-loads the EXACT title recruiters type into LinkedIn Recruiter — not creative descriptions. If a recruiter wouldn't type it, it isn't there.</span></li>
            <li className="flex gap-2"><span className="text-sd-primary">•</span> <span><strong>Hook in 2 seconds:</strong> the About section leads with an outcome, not "years of experience" or "passionate about". White space and short paragraphs render properly on the LinkedIn mobile app.</span></li>
            <li className="flex gap-2"><span className="text-sd-primary">•</span> <span><strong>One metric per paragraph:</strong> LinkedIn's algorithm favours specificity. Numbers, scope, and named companies signal seniority to both recruiters and the feed.</span></li>
            <li className="flex gap-2"><span className="text-sd-primary">•</span> <span><strong>Sounds like the target role, not aspiring to it:</strong> every rewrite is tuned so it reads like someone who already holds the role wrote it — not a career coach.</span></li>
          </ul>
          {regenError && <div className="text-[12px] text-sd-error mt-3" data-testid="linkedin-regenerate-error">{regenError}</div>}
        </div>
      )}

      <Expandable title="Headline (≤120 chars — front-loaded exact recruiter title)" testId="linkedin-headline">
        <BeforeAfter
          before={l.headline?.current}
          after={l.headline?.suggested}
          why={l.headline?.why}
          fallback="This section didn't generate."
          onRegenerate={jdId ? runRegenerate : null}
          regenerating={regenerating}
        />
        {l.headline?.suggested && (
          <div className="mt-2 flex items-center gap-2 text-[11.5px] text-sd-mid-gray" data-testid="headline-char-count">
            <span>{(l.headline.suggested || '').length} / 120 characters</span>
            {(l.headline.suggested || '').length > 120 && (
              <span className="text-sd-error font-medium">· over limit — trim to pass the LinkedIn Recruiter filter</span>
            )}
          </div>
        )}
      </Expandable>
      <Expandable title="About section (4 short paragraphs, first person)" testId="linkedin-about">
        <BeforeAfter
          before={l.about?.current}
          after={l.about?.suggested}
          why={l.about?.why}
          fallback="This section didn't generate."
          onRegenerate={jdId ? runRegenerate : null}
          regenerating={regenerating}
        />
      </Expandable>
      <Expandable title="Experience bullets (verb + outcome + metric + JD keyword)" testId="linkedin-bullets">
        {(l.bullets || []).filter(b => (b.after || '').trim()).length > 0 ? (
          <div className="space-y-3">
            {l.bullets.filter(b => (b.after || '').trim()).map((b, i) => (
              <BeforeAfter key={i} before={b.before} after={b.after} />
            ))}
          </div>
        ) : (
          <BeforeAfter
            fallback="No bullets were generated for this JD."
            onRegenerate={jdId ? runRegenerate : null}
            regenerating={regenerating}
          />
        )}
      </Expandable>
      {(l.keywords || []).length > 0 && (
        <div className="sd-card p-5 mt-5">
          <h3 className="text-[14px] font-semibold text-sd-black mb-1">Keywords to include</h3>
          <p className="text-[12px] text-sd-mid-gray mb-3">Every keyword below is a real LinkedIn Recruiter search filter. <span className="font-semibold text-sd-primary">High-volume</span> = many recruiters search for it (more competition). <span className="font-semibold text-sd-accent">Niche</span> = fewer searchers but higher intent.</p>
          <div className="flex flex-wrap gap-2" data-testid="linkedin-keywords">
            {l.keywords.map((k, i) => {
              const term = typeof k === 'string' ? k : (k?.term || '');
              const volume = typeof k === 'string' ? 'niche' : (k?.volume || 'niche');
              const why = typeof k === 'string' ? '' : (k?.why || '');
              const isHigh = volume === 'high';
              return (
                <span
                  key={i}
                  className={`inline-flex items-center gap-1.5 text-[12.5px] font-medium px-2.5 py-1 rounded-pill ${
                    isHigh
                      ? 'bg-sd-primary/10 text-sd-primary'
                      : 'bg-sd-cream text-sd-dark-gray'
                  }`}
                  title={why || undefined}
                  data-testid={`linkedin-keyword-${i}`}
                >
                  <span>{term}</span>
                  <span className={`text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-[1px] rounded-full ${
                    isHigh ? 'bg-sd-primary text-white' : 'bg-sd-mid-gray/15 text-sd-mid-gray'
                  }`}>
                    {isHigh ? 'High' : 'Niche'}
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Expandable({ title, children, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="sd-card overflow-hidden mb-3" data-testid={testId}>
      <button className="w-full p-4 text-left flex items-center justify-between" onClick={() => setOpen(o => !o)}>
        <span className="text-[14px] font-semibold text-sd-black">{title}</span>
        <span className="text-sd-mid-gray">{open ? '−' : '+'}</span>
      </button>
      {open && <div className="px-4 pb-4 animate-fade-in">{children}</div>}
    </div>
  );
}

function BeforeAfter({ before, after, why, fallback, onRegenerate, regenerating }) {
  const hasBefore = !!(before || '').trim();
  const hasAfter = !!(after || '').trim();

  // If both are empty, show a graceful fallback card with an inline
  // "Regenerate this section" button when a callback is provided. This lets
  // the user retry without hunting for the small pill button at the top.
  if (!hasBefore && !hasAfter) {
    return (
      <div
        className="p-4 rounded-[10px] bg-sd-ivory border border-dashed border-sd-light-gray flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
        data-testid="beforeafter-empty"
      >
        <div className="text-[13px] text-sd-mid-gray leading-relaxed italic flex-1 min-w-0">
          {fallback || "This section didn't generate."}
        </div>
        {onRegenerate && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRegenerate(); }}
            disabled={regenerating}
            className="sd-btn-primary inline-flex items-center gap-2 !py-1.5 !px-3 !text-[12px] shrink-0 self-start"
            data-testid="beforeafter-regenerate"
          >
            {regenerating ? (
              <><Spinner size={11} color="#FFFFFF" /> Regenerating…</>
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
                Regenerate this section
              </>
            )}
          </button>
        )}
      </div>
    );
  }
  return (
    <div>
      {hasBefore && <div className="p-3 rounded-[10px] bg-sd-ivory text-[13px] text-sd-mid-gray mb-2 line-through">{before}</div>}
      {hasAfter && (
        <div
          className="p-3 rounded-[10px] text-[13.5px] leading-relaxed whitespace-pre-line"
          style={{ background: '#E9FAEC', color: '#1F4D2A' }}
        >
          {after}
        </div>
      )}
      {why && <div className="mt-2 text-[12.5px] text-sd-mid-gray italic">Why: {why}</div>}
    </div>
  );
}
